import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  CircularProgress,
  TablePagination,
  TextField,
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  List,
  ListItem,
  ListItemText,
  Divider,
  Card,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import CloseIcon from "@mui/icons-material/Close";
const DiagnosisTable = ({
  diagnoses,
  onEdit,
  setIsCreate,
  loading,
  setIsEdit,
  onDelete,
}) => {
  const [data, setData] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [filteredData, setFilteredData] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedDiagnosis, setSelectedDiagnosis] = useState(null);

  useEffect(() => {
    if (diagnoses && Array.isArray(diagnoses)) {
      setData(diagnoses);
    }
  }, [diagnoses]);

  useEffect(() => {
    const filtered = data.filter((item) =>
      item.customer_name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredData(filtered);
    setPage(0);
  }, [searchTerm, data]);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleEditData = (row) => {
    scrollToTop();
    if (onEdit) {
      onEdit(row); // Send full row data to parent
      setIsCreate(true);
      setIsEdit(true);
    }
  };
  const scrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  const handleDelete = (row) => {
    if (onDelete) {
      onDelete(row); // Send full row data to parent
    }
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleView = (diagnosis) => {
    setSelectedDiagnosis(diagnosis);
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedDiagnosis(null);
  };

  return (
    <>
      <Paper sx={{ width: "100%", overflow: "hidden", p: 2 }}>
        {/* Search Box */}
        <Box display="flex" justifyContent="flex-end" mb={1}>
          <TextField
            size="small"
            label="Search by Customer Name"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </Box>

        <TableContainer sx={{ display: { xs: "none", md: "block" } }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "#4cb5f5" }}>
                <TableCell sx={{ color: "#fff" }}>S.No</TableCell>
                <TableCell sx={{ color: "#fff" }}>Customer Name</TableCell>
                <TableCell sx={{ color: "#fff" }}>Diagnosis</TableCell>
                <TableCell sx={{ color: "#fff" }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    <CircularProgress size={30} />
                  </TableCell>
                </TableRow>
              ) : filteredData.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No data found
                  </TableCell>
                </TableRow>
              ) : (
                filteredData
                  .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                  .map((row, index) => (
                    <TableRow key={row.diagnosis_id}>
                      <TableCell>{page * rowsPerPage + index + 1}</TableCell>
                      <TableCell>{row.customer_name}</TableCell>
                      <TableCell>{row.item_name}</TableCell>
                      <TableCell>
                        <IconButton
                          color="primary"
                          onClick={() => handleView(row)}
                        >
                          <RemoveRedEyeIcon />
                        </IconButton>
                        <IconButton
                          color="primary"
                          onClick={() => handleEditData(row)}
                        >
                          <EditIcon />
                        </IconButton>

                        <IconButton
                          color="error"
                          onClick={() => handleDelete(row)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        <Box sx={{ display: { xs: "block", md: "none" } }}>
          {loading ? (
            <Box textAlign="center" mt={2}>
              <CircularProgress size={30} />
            </Box>
          ) : filteredData.length === 0 ? (
            <Box textAlign="center" mt={2}>
              No data found
            </Box>
          ) : (
            filteredData
              .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
              .map((row, index) => (
                <Card
                  key={row.diagnosis_id}
                  sx={{
                    mb: 2,
                    p: 2,
                    boxShadow: 3,
                  }}
                >
                  <Typography variant="subtitle2" color="text.secondary">
                    S.No.:{page * rowsPerPage + index + 1}
                  </Typography>
                  <Typography variant="h6">{row.customer_name}</Typography>
                  <Typography variant="body2" sx={{ mb: 1 }}>
                    Diagnosis: {row.item_name}
                  </Typography>
                  <Box>
                    <IconButton color="primary" onClick={() => handleView(row)}>
                      <RemoveRedEyeIcon />
                    </IconButton>
                    <IconButton
                      color="primary"
                      onClick={() => handleEditData(row)}
                    >
                      <EditIcon />
                    </IconButton>
                    <IconButton color="error" onClick={() => handleDelete(row)}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </Card>
              ))
          )}
        </Box>

        {!loading && (
          <TablePagination
            component="div"
            count={filteredData.length}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Paper>

      {/* Diagnosis View Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#1976d2", // blue
            color: "white",
            position: "relative",
          }}
        >
          {/* Close Button on Right */}
          <IconButton
            edge="end"
            color="error"
            onClick={handleCloseDialog}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              fontWeight: "bold",
              fontSize: "1.5rem", // bigger size
            }}
          >
            <CloseIcon sx={{ fontSize: "1.8rem", fontWeight: "bold" }} />
          </IconButton>

          {/* Customer Name */}
          {selectedDiagnosis?.customer_name}
        </DialogTitle>

        <DialogContent dividers>
          <Typography variant="subtitle1" fontWeight="bold" mb={1}>
            Diagnosis: {selectedDiagnosis?.item_name}
          </Typography>
          <List>
            {selectedDiagnosis?.responses?.map((resp, idx) => (
              <React.Fragment key={idx}>
                <ListItem>
                  <ListItemText
                    primary={resp.questionnaries_item_name}
                    secondary={`Pian: ${resp.questionnaries_values * 10}%`}
                  />
                </ListItem>
                {idx < selectedDiagnosis.responses.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DiagnosisTable;
